import ListHeader from "@/common/components/ListHeader";
import TextField from "@/common/components/TextField";
import Table from "@/common/components/Table";
import ListFilters from "@/common/components/ListFilters";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import useDebounce from "@/common/hooks/useDebounce";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { missionsColumns } from "../../constants/columns.constant";
import { missionStatusFilterOptions } from "../../constants/missionOptions.constant";
import { useMissionsList } from "../../hooks/useMissions";
import { Link } from "react-router-dom";
import Button from "@/common/components/Button";
import { Search, Goal } from "lucide-react";
import { Option } from "@/common/types/option";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { MissionsTableTransformer } from "../../services/missionsTableTransformer";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies";
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer";

const MissionsList = () => {
  const { filterParams, setFilterParams } = useFilterParams({});

  const [search, setSearch] = useDebounce(
    (filterParams.search as string) || "",
  );
  const [searchEstablishment, setSearchEstablishment] = useDebounce('');
  const { date, formattedDate } = useFilterDate(filterParams);

  const { hasUserPermission } = useUserPermissions();
  const canCreateMission = hasUserPermission("Admin::Tmais::Missions::Create");
  const canFilterMissions = hasUserPermission("Admin::Tmais::Missions::Filter");
  const canShowMission = hasUserPermission("Admin::Tmais::Missions::Show");

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: {
      search: searchEstablishment,
      tmaisModuleEnabled: true,
    },
    mapper: EstablishmentCompanyTransformer.optionTransformer({ valueFieldName: 'matrix_branch_id' }),
  });

  const { data, isLoading, error, refetch } = useMissionsList({
    filters: {
      search,
      page: Number(filterParams.page) || 1,
      status: filterParams.status?.value,
      creatorEstablishmentCompanyBranchId: filterParams.establishment?.value,
      availableFromGteq: formattedDate.from,
      availableFromLteq: formattedDate.to,
    },
  });

  const missions = data?.data || [];
  const meta = data?.meta || { total: missions.length, total_pages: 1 };
  const currentPage = Number(filterParams.page) || 1;
  const missionTableRows = data?.rows || [];
  const rows = MissionsTableTransformer.tableTransformer({ canShowMission })(
    missionTableRows,
  );

  const createMissionButton = (
    <Button
      className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
      operationKlassToCheckPermission="Admin::Tmais::Missions::Create"
    >
      Cadastrar missão
    </Button>
  );

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<Goal className="w-10 h-10" strokeWidth={1.5} />}
        title="Missões"
        actions={
          canCreateMission ? (
            <Link to="/tmais-missions/new">{createMissionButton}</Link>
          ) : (
            createMissionButton
          )
        }
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={(error as any)?.status || 200}
        refetchFunction={refetch}
      >
        <div className="flex flex-col gap-6 h-[calc(100vh-128px)]">
          <TextField
            LeftIcon={<Search />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            value={(filterParams.search as string) || ""}
            disabled={!canFilterMissions}
            onChange={(event) => {
              const value = (event.target as HTMLInputElement).value;
              setSearch(value);
              setFilterParams((prev: any) => ({
                ...prev,
                search: value,
                page: 1,
              }));
            }}
          />

          <div className="flex items-center gap-2">
            <DatePickerWithRange
              placeholder="Data de disponibilidade"
              date={date}
              setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))}
            />

            <ListFilters
              filters={[
                {
                  className: 'min-w-40',
                  placeholder: 'Status',
                  options: missionStatusFilterOptions,
                  disabled: !canFilterMissions,
                  operationKlassToCheckPermission: 'Admin::Tmais::Missions::Filter',
                  onChange: (e: any) => setFilterParams((params) => ({ ...params, status: e?.value ? e : null })),
                  value: filterParams?.status || '',
                },
                {
                  className: 'min-w-40',
                  onChange: (e: any) =>
                    setFilterParams((params) => ({ ...params, establishment: e?.value ? e : null })),
                  onInputChange: setSearchEstablishment,
                  value: filterParams?.establishment || '',
                  options: [
                    { label: 'Todos', value: '' },
                    { label: 'Taurus (Global)', value: 'null' },
                    ...(establishmentOptions as Option[]),
                  ],
                  placeholder: 'Estabelecimento',
                  dataTestid: 'filter_establishment',
                },
              ]}
            />
          </div>

          <Table
            columns={missionsColumns}
            rows={rows}
            isLoadingData={isLoading}
            page={currentPage}
            totalPages={meta.total_pages || 1}
            totalCount={meta.total || missions.length}
            hasPagination
            setPage={(page) =>
              setFilterParams((prev: any) => ({ ...prev, page }))
            }
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default MissionsList;
